import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { climbHeading } from '../../../utils/climb-title';
import type { Climb } from '../../../types/climb';
import type { ClimbLog } from '../../../types/climb-log';
import type { TemplateId, TemplateRenderData } from './share-templates';

export const TEMPLATE_LABEL_KEYS: Record<TemplateId, string> = {
  none: 'shareClimb.templateNone',
  info: 'shareClimb.templateInfo',
  cover: 'shareClimb.templateCover',
};

/** Whether the climb has a card to show: its photo, or a board problem's wall (see `useClimbThumbnail`). */
export function climbHasCard(climb: Climb): boolean {
  const hasCover = !!(climb.image_url ?? climb.image_medium_url ?? climb.image_small_url);
  return hasCover || (climb.type === 'BoardClimb' && !!climb.board_image_url);
}

/** The layouts this climb can be drawn in: the media alone first (the default), "Photo" only with a card. */
export function availableShareTemplates(climb: Climb): TemplateId[] {
  return climbHasCard(climb) ? ['none', 'info', 'cover'] : ['none', 'info'];
}

/**
 * What the stickers made of the climb's words, and the gym's, are drawn from:
 * the climb's data and the gym's avatar, loaded once; and the layouts the
 * climb can be drawn in — the ones the advanced editor steps through.
 *
 * `assetVersion` is bumped whenever a picture finishes loading, so the static
 * draws can re-run with it.
 */
export function useShareRenderData(climb: Climb, climbLog: ClimbLog | null | undefined, gymImageUrl: string | null | undefined) {
  const { t } = useTranslation();
  const [assetVersion, setAssetVersion] = useState(0);
  const gymAvatarRef = useRef<HTMLImageElement | null>(null);

  const availableTemplates = useMemo(() => availableShareTemplates(climb), [climb]);

  // Preload gym avatar when URL changes
  useEffect(() => {
    if (!gymImageUrl) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => { gymAvatarRef.current = img; setAssetVersion(v => v + 1); };
    img.src = gymImageUrl;
  }, [gymImageUrl]);

  const sendValue = useMemo((): string => {
    const nd = t('shareClimb.sendNd');
    if (!climbLog) return nd;
    const { send_type, attempts_count } = climbLog;
    if (!send_type || send_type === 'nd') return nd;
    const key = send_type === 'on_sight' ? 'onSight' : send_type;
    const base = t(`climbLog.${key}`);
    if (send_type === 'worked' && attempts_count) return `${base} (${attempts_count})`;
    return base;
  }, [climbLog, t]);

  const labels = useMemo(() => ({
    grade: t('shareClimb.statGrade'),
    send: t('shareClimb.statSend'),
    rating: t('shareClimb.statRating'),
  }), [t]);

  // The words, which change with the climb and the language only; the avatar
  // is read when drawing, as it loads.
  const words = useMemo(() => {
    const { heading, name } = climbHeading(climb);
    return {
      // What it is called on the wall: grade, holds' colour, zone.
      name: {
        heading,
        subtitle: name,
        // Board climbs are boulder-style for display purposes.
        climbType: climb.type === 'BoardClimb' ? 'Boulder' as const : climb.type,
      },
      stats: { grade: climb.difficulty, sendValue, ratingAvg: climb.average_rating, labels },
      locationName: climb.location.name,
    };
  }, [climb, sendValue, labels]);

  const buildRenderData = useCallback((): TemplateRenderData => ({
    ...words,
    gymAvatar: gymAvatarRef.current,
  }), [words]);

  return { buildRenderData, assetVersion, availableTemplates };
}
